import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Spinner } from '@/components/Spinner';
import { useAuth } from '@/lib/auth';
import {
  callGetSessionState,
  callSubmitAnswer,
  type AnswerIndex,
  type CategoryId,
  type QuestionForClient,
} from '@/lib/api';
import { cn } from '@/lib/utils';
import { ArrowRight, BarChart3, Flag, Sparkles, X } from 'lucide-react';

interface LocState {
  questions: QuestionForClient[];
  mode: 'trial';
  warnings: string[];
}

const CATEGORY_LABEL: Record<CategoryId, string> = {
  adl: 'Activities of Daily Living',
  self_care_independence: 'Self-Care Independence',
  client_rights: 'Client Rights',
  legal_ethical: 'Legal and Ethical',
  basic_nursing: 'Basic Nursing',
  health_care_team: 'Health Care Team',
  emotional_mental: 'Emotional and Mental Health',
  communication: 'Communication',
  spiritual_cultural: 'Spiritual and Cultural',
};

const CATEGORY_SHORT: Record<CategoryId, string> = {
  adl: 'ADL',
  self_care_independence: 'Self-Care',
  client_rights: 'Client Rights',
  legal_ethical: 'Legal/Ethical',
  basic_nursing: 'Basic Nursing',
  health_care_team: 'Care Team',
  emotional_mental: 'Emotional/Mental',
  communication: 'Communication',
  spiritual_cultural: 'Spiritual/Cultural',
};

interface AnswerRecord {
  questionId: string;
  category: CategoryId;
  selectedShuffledIndex: AnswerIndex;
  correct: boolean;
  rationale?: string;
  correctShuffledIndex?: AnswerIndex;
}

// Milestones fire AFTER answering Q5, Q13, Q20 (1-indexed) — i.e. when answers
// count crosses 5, 13, 20. Q25 doesn't get a milestone screen; it goes straight
// to results.
const MILESTONES = new Set([5, 13, 20]);

// Auto-advance on correct: short pause to flash green confirmation, then move on.
const AUTO_ADVANCE_CORRECT_MS = 1300;

export function TrialExam() {
  const { sessionId = '' } = useParams();
  const loc = useLocation();
  const nav = useNavigate();
  const state = loc.state as LocState | null;
  const { user, loading: authLoading, isAnonymous } = useAuth();

  const [index, setIndex] = useState(0);
  const [pickedShuffled, setPickedShuffled] = useState<AnswerIndex | null>(null);
  const [flagged, setFlagged] = useState(false);
  const [answers, setAnswers] = useState<AnswerRecord[]>([]);
  const [showingMilestone, setShowingMilestone] = useState<number | null>(null);
  const [showExitDialog, setShowExitDialog] = useState(false);

  // Reload-resume: if React Router state is missing (page refresh), fetch the
  // slate + completed-question IDs from the server and jump to the first
  // unanswered question.
  const resume = useQuery({
    queryKey: ['session-state', sessionId, user?.uid ?? null],
    queryFn: () => callGetSessionState({ sessionId }),
    // Wait until anonymous auth is rehydrated before calling getSessionState;
    // otherwise it fires before request.auth exists and 401s during refresh.
    enabled: !state?.questions && !authLoading && !!user,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (state?.questions || !resume.data) return;
    if (resume.data.isCompleted) {
      nav(`/trial/${sessionId}/results`, { replace: true });
      return;
    }
    const completed = new Set(resume.data.completedQuestionIds);
    const startIndex = resume.data.questions.findIndex((q) => !completed.has(q.id));
    setIndex(startIndex === -1 ? resume.data.questions.length - 1 : startIndex);
    const synthesized: AnswerRecord[] = [];
    for (const q of resume.data.questions) {
      if (completed.has(q.id)) {
        synthesized.push({
          questionId: q.id,
          category: q.category,
          selectedShuffledIndex: 0,
          correct: false, // unknown on resume; tally is approximate
        });
      }
    }
    setAnswers(synthesized);
  }, [state?.questions, resume.data, sessionId, nav]);

  // ---- Hooks below run unconditionally (rules-of-hooks) ----

  const questions = state?.questions ?? resume.data?.questions;
  const total = questions?.length ?? 0;
  const current = questions?.[index];
  const submitted = current ? answers.find((a) => a.questionId === current.id) : undefined;
  const finalQ = total > 0 && index === total - 1;
  const correctSoFar = answers.filter((a) => a.correct).length;

  const submit = useMutation({
    mutationFn: () => {
      if (pickedShuffled === null) throw new Error('No selection');
      if (!current) throw new Error('Question not loaded');
      return callSubmitAnswer({
        sessionId,
        questionId: current.id,
        selectedShuffledIndex: pickedShuffled,
        flagged,
      });
    },
    onSuccess: (resp) => {
      if (!current) return;
      const record: AnswerRecord = {
        questionId: current.id,
        category: current.category,
        selectedShuffledIndex: pickedShuffled!,
        correct: resp.correct === true,
        rationale: resp.rationale,
        correctShuffledIndex: resp.correctShuffledIndex,
      };
      setAnswers((prev) => [...prev, record]);
    },
  });

  function next() {
    setPickedShuffled(null);
    setFlagged(false);
    submit.reset();
    const willBeAt = index + 1;
    if (MILESTONES.has(answers.length) && answers.length < 25) {
      setShowingMilestone(answers.length);
      return;
    }
    setIndex(willBeAt);
  }

  function dismissMilestone() {
    setShowingMilestone(null);
    setIndex((i) => i + 1);
  }

  // Auto-advance on correct.
  useEffect(() => {
    if (!submitted || submitted.correct !== true) return;
    if (showingMilestone !== null) return;
    const t = window.setTimeout(() => {
      if (finalQ) nav(`/trial/${sessionId}/results`);
      else next();
    }, AUTO_ADVANCE_CORRECT_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted?.correct, current?.id, finalQ, showingMilestone]);

  // Pre-compute milestone payload for the *current* answer count.
  const milestonePayload = useMemo(() => {
    if (showingMilestone === null) return null;
    const answered = answers.length;
    const correctCount = answers.filter((a) => a.correct).length;
    const byCat = bucketByCategory(answers);
    const strongest = pickExtreme(byCat, 'strongest');
    const weakest = pickExtreme(byCat, 'weakest');
    return { answered, correctCount, strongest, weakest, milestone: showingMilestone };
  }, [showingMilestone, answers]);

  // ---- Conditional renders below (after all hooks) ----

  if (!questions || !current) {
    if (resume.isError) {
      return (
        <div className="min-h-screen flex items-center justify-center px-4">
          <Card>
            <h1 className="text-lg font-semibold mb-2">Couldn't resume your trial</h1>
            <p className="text-sm text-stone-600 dark:text-stone-400 mb-4">
              {(resume.error as { message?: string }).message ?? 'Session not found.'}
            </p>
            <Link to="/trial/new">
              <Button>Start a new trial</Button>
            </Link>
          </Card>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner className="h-8 w-8 text-brand-600" />
      </div>
    );
  }

  // Milestone screen takes over the layout.
  if (showingMilestone !== null && milestonePayload) {
    return <MilestoneScreen payload={milestonePayload} onContinue={dismissMilestone} />;
  }

  const progressPct = Math.round(((index + (submitted ? 1 : 0)) / total) * 100);
  const accSoFarPct = answers.length > 0 ? Math.round((correctSoFar / answers.length) * 100) : null;

  return (
    <div className="h-[100dvh] bg-stone-50 dark:bg-stone-950 text-stone-900 dark:text-stone-100 flex flex-col pt-2">
      {/* Header — slim, with running score + progress. Fixed at top of the
          flex column with a small page margin (pt-2 above) so it visually
          floats off the viewport edge. */}
      <header className="flex-none bg-stone-50/95 dark:bg-stone-950/95 backdrop-blur border-b border-stone-200 dark:border-stone-800 px-4 sm:px-6 py-2.5">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between gap-3 text-xs text-stone-500 mb-1.5">
            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label="Exit the trial"
                onClick={() => setShowExitDialog(true)}
                className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 text-[12px] font-medium text-stone-700 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
              >
                <X className="h-3.5 w-3.5" /> Exit
              </button>
              <span className="inline-flex items-center gap-1.5 font-medium text-brand-700 dark:text-brand-400">
                <Sparkles className="h-3.5 w-3.5" /> Free Trial
              </span>
            </div>
            <span className="flex items-center gap-3 tabular-nums">
              {accSoFarPct !== null && (
                <span className={cn(
                  'font-semibold',
                  accSoFarPct >= 70 ? 'text-emerald-700 dark:text-emerald-400' : accSoFarPct >= 50 ? 'text-brand-700 dark:text-brand-400' : 'text-amber-700 dark:text-amber-400',
                )}>
                  {accSoFarPct}% · {correctSoFar}/{answers.length}
                </span>
              )}
              <span>{index + 1} / {total}</span>
            </span>
          </div>
          <div className="h-1.5 bg-stone-200 dark:bg-stone-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-600 transition-all duration-300"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto w-full px-4 sm:px-6 py-4">
        {/* Question card */}
        <Card className="!p-3 sm:!p-5 overflow-hidden">
          <div className="flex items-center justify-between gap-2 mb-2 sm:mb-2.5 flex-none">
            <div className="flex items-center gap-2 min-w-0">
              <span className="inline-flex items-center justify-center flex-none rounded-md bg-brand-600 text-white px-2 py-1 text-[12.5px] font-bold tabular-nums tracking-tight">
                Q{index + 1}
                <span className="text-brand-200 font-medium ml-1">/ {total}</span>
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-brand-50 text-brand-800 dark:bg-brand-900/30 dark:text-brand-300 truncate">
                {CATEGORY_LABEL[current.category]}
              </span>
            </div>
            <button
              type="button"
              aria-pressed={flagged}
              aria-label="Flag question for review"
              disabled={!!submitted}
              onClick={() => setFlagged((f) => !f)}
              className={cn(
                'inline-flex items-center justify-center h-8 w-8 rounded-md transition-colors flex-none',
                flagged
                  ? 'text-amber-600 bg-amber-50 dark:text-amber-400 dark:bg-amber-900/20'
                  : 'text-stone-500 hover:bg-stone-100 dark:hover:bg-stone-800',
                submitted && 'opacity-60 cursor-not-allowed',
              )}
            >
              <Flag className="h-4 w-4" />
            </button>
          </div>

          <div>
            <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-5 leading-snug text-stone-900 dark:text-stone-50">{current.stem}</h2>

            <ul className="space-y-2 sm:space-y-2.5">
              {current.options.map((opt, i) => {
                const isPicked = pickedShuffled === i;
                const showFeedback = !!submitted;
                const isPickedCorrect = showFeedback && submitted?.correct && isPicked;
                const isPickedWrong = showFeedback && !submitted?.correct && isPicked;
                const isCorrectAnswer = showFeedback && !submitted?.correct && submitted?.correctShuffledIndex === i;
                return (
                  <li key={i}>
                    <button
                      type="button"
                      disabled={!!submitted}
                      onClick={() => setPickedShuffled(i as AnswerIndex)}
                      className={cn(
                        'w-full text-left rounded-lg sm:rounded-xl border px-3 py-2 sm:px-4 sm:py-2.5 min-h-[44px] sm:min-h-[48px] transition-colors',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1',
                        !submitted && isPicked
                          ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30 dark:border-brand-400'
                          : 'border-stone-300 hover:border-stone-400 dark:border-stone-700 dark:hover:border-stone-600',
                        isPickedCorrect && 'border-correct-fg bg-correct-bg dark:bg-correct-bgDark dark:border-correct-fgDark',
                        isPickedWrong && 'border-incorrect-fg bg-incorrect-bg dark:bg-incorrect-bgDark dark:border-incorrect-fgDark',
                        isCorrectAnswer && 'border-correct-fg bg-correct-bg/60 dark:bg-correct-bgDark/60 dark:border-correct-fgDark',
                        submitted && !isPicked && !isCorrectAnswer && 'opacity-60',
                      )}
                    >
                      <span className="flex items-start gap-2.5 sm:gap-3">
                        <span
                          className={cn(
                            'flex-none inline-flex items-center justify-center h-5 w-5 sm:h-6 sm:w-6 rounded-full text-[11px] sm:text-xs font-semibold mt-0.5',
                            !submitted && isPicked
                              ? 'bg-brand-600 text-white'
                              : isPickedCorrect || isCorrectAnswer
                                ? 'bg-correct-fg text-white dark:bg-correct-fgDark'
                                : isPickedWrong
                                  ? 'bg-incorrect-fg text-white dark:bg-incorrect-fgDark'
                                  : 'bg-stone-200 text-stone-700 dark:bg-stone-800 dark:text-stone-300',
                          )}
                        >
                          {String.fromCharCode(65 + i)}
                        </span>
                        <span className="text-[17px] sm:text-[19px] font-semibold leading-snug text-stone-900 dark:text-stone-50">{opt}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            {submit.isError && (
              <p className="mt-2 text-sm text-rose-600 dark:text-rose-400">
                {(submit.error as { message?: string }).message ?? 'Could not submit answer.'}
              </p>
            )}
          </div>
        </Card>
        </div>
      </main>

      {/* Action bar — flush with main content (no fixed positioning, no whitespace) */}
      <div className="flex-none bg-stone-50/95 dark:bg-stone-950/95 backdrop-blur border-t border-stone-200 dark:border-stone-800 px-4 py-3">
        <div className="max-w-2xl mx-auto">
          {!submitted ? (
            <Button
              size="lg"
              loading={submit.isPending}
              disabled={pickedShuffled === null}
              onClick={() => submit.mutate()}
              className="w-full"
            >
              Submit answer
            </Button>
          ) : submitted.correct ? (
            // Auto-advance is in flight — no action needed, but show a subtle
            // "advancing" affordance so the user knows what's happening.
            <Button size="lg" className="w-full !bg-emerald-600 hover:!bg-emerald-700" disabled>
              ✓ Correct — advancing…
            </Button>
          ) : null}
          {/* Wrong answer: action is on the FeedbackModal below. */}
        </div>
      </div>

      {/* Feedback modal — only on wrong answers (correct = silent auto-advance). */}
      {submitted && !submitted.correct && (
        <TrialFeedbackModal
          rationale={submitted.rationale}
          correctAnswerText={
            typeof submitted.correctShuffledIndex === 'number'
              ? current.options[submitted.correctShuffledIndex]
              : undefined
          }
          correctAnswerLetter={
            typeof submitted.correctShuffledIndex === 'number'
              ? String.fromCharCode(65 + submitted.correctShuffledIndex)
              : undefined
          }
          isFinal={finalQ}
          onContinue={() => {
            if (finalQ) nav(`/trial/${sessionId}/results`);
            else next();
          }}
        />
      )}

      {/* Exit confirmation — anonymous trials sit in the user's account
          across reloads (resume-on-restart in startTrial), but we still let
          them bail out mid-session and come back later. */}
      {showExitDialog && (
        <TrialExitDialog
          answeredCount={answers.length}
          total={total}
          isAnonymous={isAnonymous}
          onCancel={() => setShowExitDialog(false)}
          onConfirm={() => {
            setShowExitDialog(false);
            // Anonymous: go to landing. Signed-in: go to dashboard. Either
            // way, the trial session stays in-progress in Firestore so a
            // refresh of /trial/:sessionId resumes it.
            nav(isAnonymous ? '/' : '/dashboard');
          }}
        />
      )}
    </div>
  );
}

function TrialExitDialog({
  answeredCount,
  total,
  isAnonymous,
  onCancel,
  onConfirm,
}: {
  answeredCount: number;
  total: number;
  isAnonymous: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="trial-exit-title"
      className="fixed inset-0 z-50 flex items-center justify-center px-3 sm:px-4 bg-stone-900/40 dark:bg-stone-950/60 backdrop-blur-sm"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white dark:bg-stone-900 shadow-2xl ring-1 ring-stone-200 dark:ring-stone-800 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="trial-exit-title" className="text-lg font-semibold mb-1.5">
          Exit the trial?
        </h2>
        <p className="text-sm text-stone-600 dark:text-stone-400 mb-4">
          Your <strong>{answeredCount} of {total}</strong> answered questions are saved.
          {isAnonymous
            ? ' Come back to this device anytime to continue — or create a free account to keep your progress on every device.'
            : ' Resume from your dashboard whenever.'}
        </p>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button variant="ghost" onClick={onCancel}>Keep going</Button>
          <Button onClick={onConfirm}>Save &amp; exit →</Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
//  Wrong-answer modal — same vibe as Exam.tsx FeedbackModal
//  but localised to TrialExam (no shared component yet).
// ============================================================

function TrialFeedbackModal({
  rationale,
  correctAnswerText,
  correctAnswerLetter,
  isFinal,
  onContinue,
}: {
  rationale?: string;
  correctAnswerText?: string;
  correctAnswerLetter?: string;
  isFinal: boolean;
  onContinue: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="trial-feedback-title"
      className="fixed inset-0 z-50 flex items-center justify-center px-3 sm:px-4 bg-stone-900/40 dark:bg-stone-950/60 backdrop-blur-sm"
    >
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-stone-900 shadow-2xl ring-1 ring-rose-200 dark:ring-rose-800 overflow-hidden">
        <div className="px-5 py-4 flex items-center gap-3 bg-rose-50 dark:bg-rose-950/50">
          <div className="flex-none inline-flex items-center justify-center h-10 w-10 rounded-full text-white bg-rose-500">
            <X className="h-6 w-6" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 id="trial-feedback-title" className="text-lg font-bold leading-tight text-rose-900 dark:text-rose-100">
              Not quite
            </h3>
            <p className="text-[12px] mt-0.5 text-rose-700 dark:text-rose-300">
              Read the explanation, then continue.
            </p>
          </div>
        </div>

        <div className="px-5 py-4 space-y-3 max-h-[55vh] overflow-y-auto">
          {correctAnswerText && correctAnswerLetter && (
            <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-950/30 p-3">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700 dark:text-emerald-400 mb-1">
                Correct answer
              </div>
              <div className="flex items-start gap-2">
                <span className="flex-none inline-flex items-center justify-center h-5 w-5 rounded-full bg-emerald-500 text-white text-[11px] font-semibold mt-0.5">
                  {correctAnswerLetter}
                </span>
                <span className="text-sm text-emerald-900 dark:text-emerald-100 leading-snug">{correctAnswerText}</span>
              </div>
            </div>
          )}
          {rationale && (
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-stone-500 mb-1">Why</div>
              <p className="text-[14px] sm:text-[15px] text-stone-800 dark:text-stone-100 leading-relaxed">{rationale}</p>
            </div>
          )}
        </div>

        <div className="px-5 py-3 sm:py-4 border-t border-stone-100 dark:border-stone-800 flex items-center justify-end">
          <Button onClick={onContinue} className="min-w-[140px] !bg-emerald-600 hover:!bg-emerald-700 active:!bg-emerald-800">
            {isFinal ? 'See your results →' : 'Next question →'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
//  Milestone screen — celebrates Q5, Q13, Q20 progress checks
// ============================================================

interface MilestonePayload {
  milestone: number;
  answered: number;
  correctCount: number;
  strongest?: { category: CategoryId; correct: number; total: number };
  weakest?: { category: CategoryId; correct: number; total: number };
}

function MilestoneScreen({
  payload,
  onContinue,
}: {
  payload: MilestonePayload;
  onContinue: () => void;
}) {
  const { milestone, answered, correctCount, strongest, weakest } = payload;
  const accPct = answered > 0 ? Math.round((correctCount / answered) * 100) : 0;

  let headline: string;
  let subhead: string;
  let icon: React.ReactNode = <Sparkles className="h-9 w-9 text-brand-600" />;
  if (milestone === 5) {
    headline = "🎉 You're 20% in!";
    subhead = "Great pace. Keep going.";
  } else if (milestone === 13) {
    headline = '📊 Halfway there';
    subhead = 'Most students who practice their weak areas improve their pass rate by 34%.';
    icon = <BarChart3 className="h-9 w-9 text-brand-600" />;
  } else {
    headline = '🚀 Almost done!';
    subhead = 'Just 5 more questions before your full readiness report.';
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-stone-50 via-white to-brand-50/40 dark:from-stone-950 dark:via-stone-950 dark:to-brand-900/20">
      <div className="max-w-md w-full">
        <Card className="text-center !p-6 sm:!p-7">
          <div className="flex justify-center mb-3">{icon}</div>
          <h1 className="text-2xl font-bold mb-1">{headline}</h1>
          <p className="text-sm text-stone-600 dark:text-stone-400 mb-5">{subhead}</p>

          <div className="rounded-xl bg-stone-100 dark:bg-stone-800/60 p-4 mb-4">
            <p className="text-xs uppercase tracking-wide text-stone-500 mb-1">Your accuracy so far</p>
            <p className={cn(
              'text-4xl font-bold tabular-nums',
              accPct >= 70 ? 'text-emerald-700 dark:text-emerald-400' : accPct >= 50 ? 'text-brand-700 dark:text-brand-400' : 'text-amber-700 dark:text-amber-400',
            )}>
              {accPct}%
            </p>
            <p className="text-xs text-stone-500 mt-1">
              {correctCount} of {answered} correct
            </p>
          </div>

          {strongest && (
            <p className="text-sm text-emerald-700 dark:text-emerald-400 mb-1.5">
              💪 Strongest so far: <strong>{CATEGORY_SHORT[strongest.category]}</strong> ({strongest.correct}/{strongest.total})
            </p>
          )}
          {weakest && weakest.category !== strongest?.category && (
            <p className="text-sm text-amber-700 dark:text-amber-400 mb-4">
              📚 Practice more: <strong>{CATEGORY_SHORT[weakest.category]}</strong> ({weakest.correct}/{weakest.total})
            </p>
          )}

          <Button size="lg" onClick={onContinue} className="w-full mt-2 !bg-emerald-600 hover:!bg-emerald-700">
            Continue
            <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </Card>
      </div>
    </div>
  );
}

function bucketByCategory(answers: AnswerRecord[]) {
  const m = new Map<CategoryId, { correct: number; total: number }>();
  for (const a of answers) {
    const prev = m.get(a.category) ?? { correct: 0, total: 0 };
    prev.total += 1;
    if (a.correct) prev.correct += 1;
    m.set(a.category, prev);
  }
  return m;
}

function pickExtreme(
  m: Map<CategoryId, { correct: number; total: number }>,
  which: 'strongest' | 'weakest',
): { category: CategoryId; correct: number; total: number } | undefined {
  let best: { category: CategoryId; correct: number; total: number; acc: number } | undefined;
  for (const [cat, v] of m) {
    if (v.total === 0) continue;
    const acc = v.correct / v.total;
    if (!best) {
      best = { category: cat, ...v, acc };
      continue;
    }
    const better = which === 'strongest' ? acc > best.acc : acc < best.acc;
    if (better) best = { category: cat, ...v, acc };
  }
  if (!best) return undefined;
  return { category: best.category, correct: best.correct, total: best.total };
}
